import React, { useState, useEffect } from "react";

import Joi from "joi-browser";
import Select from "react-select";
import { Trans, useTranslation } from "react-i18next";

import UserSchema from "../../schemas/user.schema";
import UsersApi from "../../API/users.api";
import { SuccessAlert } from "../../utils/Alerts";
import LookupsApi from "../../API/lookups.api";
import { getReactSelectTheme } from "../../utils/ReactSelectStyles";
import PasswordWrapper from "../../components/Inputs/PasswordWrapper";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { getObject } from "../../utils/StorageHelpers";
// import LoadingOverlay from "../../components/Loaders/LoadingOverlay";

const ManageSecurity = () => {
    //General States
    const [isLoading, setIsLoading] = useState(false);

    //Change Password Form States
    const [passwordFormData, setPasswordFormData] = useState({});
    const [passwordError, setPasswordError] = useState({});
    const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
    const [isPasswordSubmitted, setIsPasswordSubmitted] = useState(false);
    const [isPasswordFormValid, setIsPasswordFormValid] = useState(false);

    //Change Recovery Questions Form States
    const [questionsFormData, setQuestionsFormData] = useState({});
    const [questionOptions, setQuestionOptions] = useState([]);
    const [questionsError, setQuestionsError] = useState({});
    const [isQuestingSubmitting, setIsQuestingSubmitting] = useState(false);
    const [isQuestionSubmitted, setIsQuestionSubmitted] = useState(false);
    const [isQuestionFormValid, setIsQuestionFormValid] = useState(false);

    const currentUser = getObject("user");

    //Localization
    const { t } = useTranslation();

    useEffect(() => {
        getRecoveryQuestions();
    }, []);

    useEffect(() => {
        validatePasswordForm();
    }, [passwordFormData]);

    useEffect(() => {
        validateQuestionForm();
    }, [questionsFormData]);

    const getRecoveryQuestions = async () => {
        setIsLoading(true);
        const result = await LookupsApi.getRecoveryQuestions();
        setQuestionOptions(result);
        setIsLoading(false);
    };

    const handleSubmitPasswordChange = async () => {
        setIsPasswordSubmitted(true);

        if (!isPasswordFormValid) return;
        setIsPasswordSubmitting(true);

        let payload = { ...passwordFormData };
        delete payload.newPasswordConfirmation;

        const result = await UsersApi.changeUserPassword(payload);

        if (result.modified) {
            SuccessAlert(t("passwordChangedSuccessMessage"));
            setPasswordFormData({});
            setIsPasswordSubmitted(false);
            const email = currentUser?.email || "unknown";
            LogCapture.info(`password changed for user with email ${email}`);
        }
        setIsPasswordSubmitting(false);
    };

    const handleSubmitQuestionChange = async () => {
        setIsQuestionSubmitted(true);

        if (!isQuestionFormValid) return;
        setIsQuestingSubmitting(true);

        const result = await UsersApi.changeRecoveryQuestion(questionsFormData);

        if (result.modified) {
            SuccessAlert(t("questionChangedSuccessMessage"));
            setQuestionsFormData({});
            setIsQuestionSubmitted(false);
        }

        setIsQuestingSubmitting(false);
    };

    const handleInputChange = (e, setter) => {
        setter((prevVals) => {
            return { ...prevVals, [e.target.name]: e.target.value };
        });
    };

    const handleSelectChange = (selectedOption, action, setter) => {
        setter((prevVals) => {
            return { ...prevVals, [action.name]: selectedOption.label };
        });
    };

    const getSubmitButtonContent = (isSubmitting, actionName) => {
        return isSubmitting ? (
            <>
                <span className="animate-spin mr-3 inline-flex">
                    <i className="fas fa-sync"></i>
                </span>
                <Trans i18nKey="requesting">Requesting...</Trans>
            </>
        ) : (
            actionName || <Trans i18nKey="submit">Submit</Trans>
        );
    };

    const validatePasswordForm = () => {
        const valid = Joi.validate(
            passwordFormData,
            UserSchema.ChangePasswordSchema,
            {
                abortEarly: false,
                allowUnknown: false,
            }
        );
        const newErrorObject = {};
        if (valid.error) {
            valid.error.details.forEach((err) => {
                newErrorObject[err.path.join(".")] = err.message;
            });
            setIsPasswordFormValid(false);
        } else {
            setIsPasswordFormValid(true);
        }
        setPasswordError(newErrorObject);
    };

    const validateQuestionForm = () => {
        const valid = Joi.validate(
            questionsFormData,
            UserSchema.changeQuestionSchema,
            {
                abortEarly: false,
                allowUnknown: false,
            }
        );
        const newErrorObject = {};
        if (valid.error) {
            valid.error.details.forEach((err) => {
                newErrorObject[err.path.join(".")] = err.message;
            });
            setIsQuestionFormValid(false);
        } else {
            setIsQuestionFormValid(true);
        }
        setQuestionsError(newErrorObject);
    };

    const isPasswordFormDisabled = () => {
        return (
            !(
                passwordFormData.newPassword &&
                passwordFormData.oldPassword &&
                passwordFormData.newPasswordConfirmation
            ) || isPasswordSubmitting
        );
    };

    const isQuestionFormDisabled = () => {
        return (
            !(
                questionsFormData.password &&
                questionsFormData.recoveryQuestion &&
                questionsFormData.answer
            ) || isQuestingSubmitting
        );
    };

    const showValidationError = (optionName, errorState, isSubmitted) => {
        return errorState[optionName] && isSubmitted ? (
            <div className="invalid-input-message flex items-center">
                <i className="fas fa-exclamation-triangle"></i>
                <span className="invalid-input-message-text">
                    {errorState[optionName]}
                </span>
            </div>
        ) : (
            <></>
        );
    };

    const passwordFields = [
        {
            isRequired: true,
            label: t("oldPassword"),
            input: (
                <>
                    <PasswordWrapper>
                        <input
                            type="password"
                            className="input-element"
                            name="oldPassword"
                            placeholder={t("passwordPlaceholder")}
                            value={passwordFormData?.oldPassword || ""}
                            onChange={(e) =>
                                handleInputChange(e, setPasswordFormData)
                            }
                        />
                    </PasswordWrapper>
                    {showValidationError(
                        "oldPassword",
                        passwordError,
                        isPasswordSubmitted
                    )}
                </>
            ),
        },
        {
            isRequired: true,
            label: t("newPassword"),
            input: (
                <>
                    <PasswordWrapper>
                        <input
                            type="password"
                            className="input-element"
                            name="newPassword"
                            placeholder={t("newPasswordPlaceholder")}
                            value={passwordFormData?.newPassword || ""}
                            onChange={(e) =>
                                handleInputChange(e, setPasswordFormData)
                            }
                        />
                    </PasswordWrapper>
                    {showValidationError(
                        "newPassword",
                        passwordError,
                        isPasswordSubmitted
                    )}
                </>
            ),
        },
        {
            isRequired: true,
            label: t("confirmNewPassword"),
            input: (
                <>
                    <PasswordWrapper>
                        <input
                            type="password"
                            className="input-element"
                            name="newPasswordConfirmation"
                            placeholder={t(
                                "newPasswordConfirmationPlaceholder"
                            )}
                            value={
                                passwordFormData?.newPasswordConfirmation || ""
                            }
                            onChange={(e) =>
                                handleInputChange(e, setPasswordFormData)
                            }
                        />
                    </PasswordWrapper>
                    {showValidationError(
                        "newPasswordConfirmation",
                        passwordError,
                        isPasswordSubmitted
                    )}
                </>
            ),
        },
    ];

    const questionsFields = [
        {
            isRequired: true,
            label: t("currentPassword"),
            input: (
                <>
                    <PasswordWrapper>
                        <input
                            type="password"
                            className="input-element"
                            name="password"
                            placeholder={t("passwordPlaceholder")}
                            value={questionsFormData?.password || ""}
                            onChange={(e) =>
                                handleInputChange(e, setQuestionsFormData)
                            }
                        />
                    </PasswordWrapper>
                    {showValidationError(
                        "password",
                        questionsError,
                        isQuestionSubmitted
                    )}
                </>
            ),
        },
        {
            isRequired: true,
            label: t("recoveryQuestion"),
            input: (
                <>
                    <Select
                        onChange={(selectedOption, action) =>
                            handleSelectChange(
                                selectedOption,
                                action,
                                setQuestionsFormData
                            )
                        }
                        placeholder={
                            isLoading
                                ? t("loadingPlaceholder", {
                                      entity: "Question",
                                  })
                                : t("selectPlaceholder", {
                                      entity: "Question",
                                  })
                        }
                        options={questionOptions}
                        value={
                            questionsFormData.recoveryQuestion
                                ? {
                                      label: questionsFormData.recoveryQuestion,
                                      value: questionsFormData.recoveryQuestion,
                                  }
                                : ""
                        }
                        name="recoveryQuestion"
                        isDisabled={isLoading}
                        isLoading={isLoading}
                        theme={getReactSelectTheme}
                    />
                    {showValidationError(
                        "recoveryQuestion",
                        questionsError,
                        isQuestionSubmitted
                    )}
                </>
            ),
        },
        {
            isRequired: true,
            label: t("answer"),
            input: (
                <>
                    <input
                        type="text"
                        className="input-element"
                        name="answer"
                        placeholder={t("questionPlaceholder")}
                        value={questionsFormData?.answer || ""}
                        onChange={(e) =>
                            handleInputChange(e, setQuestionsFormData)
                        }
                    />
                    {showValidationError(
                        "answer",
                        questionsError,
                        isQuestionSubmitted
                    )}
                </>
            ),
        },
    ];

    const renderFields = (fields) => {
        return (
            fields &&
            fields.map((inputField, index) => {
                return (
                    <div className="input-group" key={index}>
                        <label
                            className={`uppercase text-xs ${
                                inputField?.isRequired && "required"
                            }`}
                        >
                            {inputField?.label}
                        </label>
                        {inputField?.input}
                    </div>
                );
            })
        );
    };
    return (
        <>
            <header className="main-content-header flex items-center justify-between">
                <h1 className="h1 capitalize">
                    <Trans i18nKey="manageSecurity">Manage Security</Trans>
                </h1>
            </header>
            <div className="card-table shadow">
                <div className="upload-img-box flex">
                    <div className="w-full">
                        <h3 className="medium-title-bold capitalize">
                            <Trans i18nKey="changePassword">
                                Change Password
                            </Trans>
                        </h3>
                        <hr className="mt-2 mb-5" />
                        {renderFields(passwordFields)}
                        <div className="flex justify-end">
                            <button
                                onClick={handleSubmitPasswordChange}
                                disabled={isPasswordFormDisabled()}
                                className={`default-orange-btn ${
                                    isPasswordFormDisabled() ? "disabled" : ""
                                }`}
                            >
                                {getSubmitButtonContent(isPasswordSubmitting)}
                            </button>
                        </div>
                        <h3 className="medium-title-bold capitalize pt-4">
                            <Trans i18nKey="changeRecoveryQuestion">
                                Change Recovery Question
                            </Trans>
                        </h3>
                        <hr className="mt-2 mb-5" />
                        {renderFields(questionsFields)}
                        <div className="flex justify-end">
                            <button
                                onClick={handleSubmitQuestionChange}
                                disabled={isQuestionFormDisabled()}
                                className={`default-orange-btn ${
                                    isQuestionFormDisabled() ? "disabled" : ""
                                }`}
                            >
                                {getSubmitButtonContent(isQuestingSubmitting)}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ManageSecurity;
